;(function() {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════════════
  // CONSTANTS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const CONFIG = {
    RETRY_INTERVAL_MS: 100,
    MAX_RETRIES: 15,
    DEBOUNCE_DELAY_MS: 50,
    INTERSECTION_ROOT_MARGIN: '0px 0px -40% 0px', // Show sticky when main form is 40% out of view
    INTERSECTION_THRESHOLD: 0,
    DEFAULT_PURCHASE_OPTION: 'subscription',
    SESSION_STORAGE_KEY: 'purchaseOption',
    PRICE_PRECISION: 100 // For cents conversion
  };

  const SELECTORS = {
    PRODUCT_DATA: 'script[data-product-json]',
    VARIANT_SUBSCRIPTION_DATA: '#VariantSubscriptionData',
    VARIANT_INPUT: 'form[action^="/cart/add"] input[name="id"]',
    CURRENT_PRICE: '.custom-current-price',
    COMPARE_PRICE: '.custom-compare-at',
    BOX_SAVE_BADGE: '.box-save-badge',
    VARIANT_LABELS: 'fieldset.variant-option--buttons .variant-option__button-label',
    VARIANT_RADIO: 'input[type="radio"][data-variant-id]',
    SUBIFY_DISCOUNT_BADGE: '.subify-max-discount-badge-percentage',
    SUBIFY_CHOICE_WRAPPER: '.subify-choice-box-wrapper',
    SUBIFY_CHOICE_PRICE: '.subify-choice-price',
    SUBIFY_CHOICE_COMPARE: '.subify-choice-compare',
    SUBIFY_SELLING_PLAN_WRAPPER: '.subify-selling-plan-whole-wrapper',
    SUBIFY_ONE_TIME_TEXT: '#subify-widget-one-time-purchase-text',
    SUBIFY_SUBSCRIPTION_TEXT: '#subify-widget-subscription-text',
    CUSTOM_SELLING_PLAN_LIST: '.custom-selling-plan__list',
    CUSTOM_VARIANT_DESC: '.custom-variant-desc',
    STICKY_ATC: '#StickyATC',
    STICKY_PRICE_SOURCE: '[data-sticky-price-source]',
    STICKY_PRICE_TARGET: '[data-sticky-price]'
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STATE & INITIALIZATION GUARD
  // ═══════════════════════════════════════════════════════════════════════════
  
  const state = {
    productData: null,
    metaData: null,
    currency: 'USD',
    observers: [],
    isInitialized: false,
    retryHandle: null
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITY FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Format cents to currency string
   * @param {number} cents - Amount in cents
   * @returns {string} Formatted currency string
   */
  function formatCurrency(cents) {
    if (!Number.isFinite(cents)) cents = 0;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: state.currency
    }).format(cents / CONFIG.PRICE_PRECISION);
  }

  /**
   * Get current variant ID from form
   * @returns {number|null}
   */
  function getVariantId() {
    const input = document.querySelector(SELECTORS.VARIANT_INPUT);
    return input ? Number(input.value) : null;
  }

  /**
   * Convert string price to cents (integer)
   * Handles various formats: "$34.99", "34.99", "34,99"
   * @param {string|number} val - Price value
   * @returns {number|null} Price in cents or null
   */
  function toCents(val) {
    if (val == null || val === '') return null;
    
    // Remove currency symbols and convert commas to periods
    const cleaned = String(val).replace(/[^0-9.]/g, '');
    const num = parseFloat(cleaned);
    
    if (!Number.isFinite(num)) return null;
    
    // Use integer math to avoid floating point precision issues
    return Math.round(num * CONFIG.PRICE_PRECISION);
  }

  /**
   * Convert string to percentage number
   * @param {string|number} val - Percentage value (e.g., "15%" or 15)
   * @returns {number} Percentage as number (0 if invalid)
   */
  function toPercent(val) {
    if (val == null || val === '') return 0;
    
    const cleaned = String(val).replace(/[^0-9.]/g, '');
    const num = parseFloat(cleaned);
    
    return Number.isFinite(num) ? num : 0;
  }

  /**
   * Get current purchase option from session storage
   * @returns {string} 'subscription' or 'one-time'
   */
  function getPurchaseOption() {
    return sessionStorage.getItem(CONFIG.SESSION_STORAGE_KEY) || CONFIG.DEFAULT_PURCHASE_OPTION;
  }

  /**
   * Set purchase option in session storage
   * @param {string} option - 'subscription' or 'one-time'
   */
  function setPurchaseOption(option) {
    sessionStorage.setItem(CONFIG.SESSION_STORAGE_KEY, option);
  }

  /**
   * Debounce function execution
   * @param {Function} func - Function to debounce
   * @param {number} wait - Delay in milliseconds
   * @returns {Function} Debounced function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Safely create text node to prevent XSS
   * @param {string} tag - HTML tag name
   * @param {string} text - Text content
   * @param {string} className - CSS class name
   * @returns {HTMLElement}
   */
  function createTextElement(tag, text, className = '') {
    const el = document.createElement(tag);
    if (className) el.className = className;
    el.textContent = text;
    return el;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Initialize session storage with default purchase option
   */
  function initSessionStorage() {
    if (!sessionStorage.getItem(CONFIG.SESSION_STORAGE_KEY)) {
      sessionStorage.setItem(CONFIG.SESSION_STORAGE_KEY, CONFIG.DEFAULT_PURCHASE_OPTION);
    }
  }

  /**
   * Load and parse product data from DOM
   * @returns {boolean} Success status
   */
  function loadProductData() {
    const productDataEl = document.querySelector(SELECTORS.PRODUCT_DATA);
    const variantSubDataEl = document.querySelector(SELECTORS.VARIANT_SUBSCRIPTION_DATA);
    
    if (!productDataEl || !variantSubDataEl) {
      console.warn('custom.js: Product data elements not found');
      return false;
    }

    try {
      state.productData = JSON.parse(productDataEl.textContent);
      state.metaData = JSON.parse(variantSubDataEl.textContent);
      state.currency = state.metaData.currency || 'USD';
      delete state.metaData.currency;
      return true;
    } catch (error) {
      console.error('custom.js: Failed to parse product data', error);
      
      // Show user-facing error
      const priceEl = document.querySelector(SELECTORS.CURRENT_PRICE);
      if (priceEl) {
        priceEl.textContent = 'Price unavailable';
        priceEl.style.color = '#999';
      }
      
      return false;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDERING FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Render current and compare-at prices
   */
  function renderPrices() {
    const vid = getVariantId();
    if (!vid) return;

    const variant = state.productData.variants.find(v => v.id === vid);
    if (!variant) return;

    const metaFields = state.metaData[vid] || {};
    const isOneTime = getPurchaseOption() === 'one-time';

    // Determine current price (prefer metafields, fallback to variant price)
    const metaFieldPrice = toCents(isOneTime ? metaFields.one_time_price : metaFields.subscribe_price);
    const currentPrice = metaFieldPrice ?? Math.round(variant.price);

    // Determine compare-at price
    const metaFieldCompare = toCents(metaFields.compare_at_price);
    const comparePrice = metaFieldCompare ?? Math.round(variant.compare_at_price || variant.price);

    // Update current price
    const currentPriceEl = document.querySelector(SELECTORS.CURRENT_PRICE);
    if (currentPriceEl) {
      currentPriceEl.textContent = formatCurrency(currentPrice);
    }

    // Update compare-at price (only if greater than current)
    const comparePriceEl = document.querySelector(SELECTORS.COMPARE_PRICE);
    if (comparePriceEl) {
      if (comparePrice > currentPrice) {
        comparePriceEl.textContent = formatCurrency(comparePrice);
        comparePriceEl.style.textDecoration = 'line-through';
        comparePriceEl.style.opacity = '0.6';
        comparePriceEl.style.marginRight = '0.5em';
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.style.textDecoration = '';
        comparePriceEl.style.opacity = '';
        comparePriceEl.style.marginRight = '';
      }
    }
  }

  /**
   * Add savings badges to variant option boxes
   */
  function decorateBoxOptions() {
    // Remove existing badges to prevent duplicates
    document.querySelectorAll(SELECTORS.BOX_SAVE_BADGE).forEach(el => el.remove());

    const purchaseOption = getPurchaseOption();
    const savingsKey = purchaseOption === 'subscription'
      ? 'subscriptions_saving_amount'
      : 'one_time_saving_amount';

    const labels = document.querySelectorAll(SELECTORS.VARIANT_LABELS);
    if (!labels.length) return;

    labels.forEach(label => {
      // Find variant ID from radio button
      const radio = label.querySelector(SELECTORS.VARIANT_RADIO);
      const variantId = radio ? Number(radio.dataset.variantId) : null;

      // Fallback: match by option text
      let metaFields;
      if (variantId) {
        metaFields = state.metaData[variantId];
      } else {
        const optionText = label.querySelector('.variant-option__button-label__text')?.textContent?.trim();
        const variant = state.productData?.variants?.find(v => v?.options?.[0] === optionText);
        metaFields = variant ? state.metaData[variant.id] : null;
      }

      const savingsAmount = toCents(metaFields?.[savingsKey]);
      if (!savingsAmount || savingsAmount <= 0) return;

      // Create and insert badge
      const badge = createTextElement('span', `Save ${formatCurrency(savingsAmount)}`, 'box-save-badge');

      const textSpan = label.querySelector('.variant-option__button-label__text');
      if (textSpan && textSpan.parentNode === label) {
        textSpan.insertAdjacentElement('afterend', badge);
      } else {
        label.appendChild(badge);
      }
    });
  }

  /**
   * Update Subify discount badge percentage
   */
  function updateSubifyDiscountBadge() {
    const vid = getVariantId();
    if (!vid) return;

    const metaFields = state.metaData[vid] || {};
    const discountPercent = toPercent(metaFields.subscriptions_discount_pct);
    
    const badgeSpan = document.querySelector(SELECTORS.SUBIFY_DISCOUNT_BADGE);
    if (badgeSpan && discountPercent) {
      badgeSpan.textContent = `${discountPercent}%`;
    }
  }

  /**
   * Add compare-at prices to Subify choice boxes
   */
  function decorateSubifyComparePrices() {
    const vid = getVariantId();
    if (!vid) return;

    const metaFields = state.metaData[vid] || {};
    const variant = state.productData.variants.find(v => v.id === vid);
    if (!variant) return;

    const comparePrice = toCents(metaFields.compare_at_price) ?? 
                        Math.round(variant.compare_at_price || variant.price);

    document.querySelectorAll(SELECTORS.SUBIFY_CHOICE_WRAPPER).forEach(wrapper => {
      const priceSpan = wrapper.querySelector(SELECTORS.SUBIFY_CHOICE_PRICE);
      if (!priceSpan) return;

      // Parse displayed price
      const displayedPriceCents = Math.round(
        Number(priceSpan.textContent.replace(/[^0-9.]/g, '')) * CONFIG.PRICE_PRECISION
      );

      // Only show compare price if it's higher
      if (comparePrice <= displayedPriceCents) return;
      
      // Don't duplicate
      if (wrapper.querySelector(SELECTORS.SUBIFY_CHOICE_COMPARE)) return;

      const compareEl = createTextElement('span', formatCurrency(comparePrice), 'subify-choice-compare');
      priceSpan.parentNode.appendChild(compareEl);
    });
  }

  /**
   * Inject selling plan details
   */
  function injectSellingPlanDetails() {
    const vid = getVariantId();
    const metaFields = state.metaData[vid] || {};
    const description = (metaFields.variant_amount_description || '').trim();
    const purchaseOption = getPurchaseOption();

    // 1) Subscription selling plan list
    document.querySelectorAll(SELECTORS.SUBIFY_SELLING_PLAN_WRAPPER).forEach(wrapper => {
      if (wrapper.querySelector(SELECTORS.CUSTOM_SELLING_PLAN_LIST)) return;

      const ul = document.createElement('ul');
      ul.className = 'custom-selling-plan__list';

      const items = [];
      if (description) {
        const li = createTextElement('li', `✓ ${description}`);
        items.push(li);
      }
      
      ['Ships Monthly', 'Free Shipping', 'Cancel or Pause Anytime'].forEach(text => {
        const li = createTextElement('li', `✓ ${text}`);
        items.push(li);
      });

      items.forEach(li => ul.appendChild(li));
      wrapper.innerHTML = '';
      wrapper.appendChild(ul);
    });

    // 2) One-time purchase variant description
    document.querySelectorAll(SELECTORS.SUBIFY_CHOICE_WRAPPER).forEach(box => {
      const isOneTimeBox = !!box.querySelector(SELECTORS.SUBIFY_ONE_TIME_TEXT);
      const existingDesc = box.querySelector(SELECTORS.CUSTOM_VARIANT_DESC);

      if (isOneTimeBox && purchaseOption === 'one-time' && description) {
        if (!existingDesc) {
          const inner = box.querySelector(':scope > div:not([class])');
          if (inner) {
            const p = createTextElement('p', `✓ ${description}`, 'custom-variant-desc');
            inner.insertAdjacentElement('afterend', p);
          }
        }
      } else if (existingDesc) {
        existingDesc.remove();
      }
    });
  }

  /**
   * Execute all rendering functions
   */
  function fullPaint() {
    if (!state.productData || !state.metaData) return;
    
    renderPrices();
    decorateBoxOptions();
    updateSubifyDiscountBadge();
    decorateSubifyComparePrices();
    injectSellingPlanDetails();
  }

  // Debounced version for frequent calls
  const debouncedFullPaint = debounce(fullPaint, CONFIG.DEBOUNCE_DELAY_MS);

  // ═══════════════════════════════════════════════════════════════════════════
  // EVENT HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Handle Subify purchase option toggle
   */
  function handleSubifyToggle(event) {
    const target = event.target.closest(SELECTORS.SUBIFY_CHOICE_WRAPPER + ' div');
    if (!target) return;

    const isOneTime = !!target.querySelector(SELECTORS.SUBIFY_ONE_TIME_TEXT);
    setPurchaseOption(isOneTime ? 'one-time' : 'subscription');
    
    debouncedFullPaint();
  }

  /**
   * Handle native variant radio change
   */
  function handleVariantChange(event) {
    if (event.target.matches('fieldset.variant-option--buttons input[type="radio"]')) {
      debouncedFullPaint();
    }
  }

  /**
   * Handle variant input value mutation
   */
  function handleVariantMutation(mutations) {
    const hasValueChange = mutations.some(m => m.attributeName === 'value');
    if (!hasValueChange) return;

    // Re-apply last selected purchase option
    const purchaseOption = getPurchaseOption();
    const allWrappers = Array.from(document.querySelectorAll(SELECTORS.SUBIFY_CHOICE_WRAPPER));
    
    const targetWrapper = purchaseOption === 'one-time'
      ? allWrappers.find(w => w.querySelector(SELECTORS.SUBIFY_ONE_TIME_TEXT))
      : allWrappers.find(w => w.querySelector(SELECTORS.SUBIFY_SUBSCRIPTION_TEXT));

    if (targetWrapper) {
      const checkbox = targetWrapper.querySelector('.checkbox-label');
      if (checkbox) checkbox.click();
    }

    // Re-render after state sync
    setTimeout(() => {
      renderPrices();
      decorateBoxOptions();
      updateSubifyDiscountBadge();
      decorateSubifyComparePrices();
    }, 100);
  }

  /**
   * Handle late DOM insertions (e.g., Subify lazy load)
   */
  function handleDomMutations(mutations) {
    const hasSellingPlan = mutations.some(mutation => {
      return Array.from(mutation.addedNodes).some(node => {
        if (!(node instanceof HTMLElement)) return false;
        return node.matches(SELECTORS.SUBIFY_SELLING_PLAN_WRAPPER) ||
               node.querySelector?.(SELECTORS.SUBIFY_SELLING_PLAN_WRAPPER);
      });
    });

    if (hasSellingPlan) {
      debouncedFullPaint();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SETUP & CLEANUP
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Setup event listeners
   */
  function setupEventListeners() {
    // Subify toggle clicks
    document.body.addEventListener('click', handleSubifyToggle);

    // Native variant changes
    document.addEventListener('change', handleVariantChange);

    // Variant input mutations
    const variantInput = document.querySelector(SELECTORS.VARIANT_INPUT);
    if (variantInput) {
      const observer = new MutationObserver(handleVariantMutation);
      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ['value']
      });
      state.observers.push(observer);
    }

    // DOM mutations for late insertions
    const domObserver = new MutationObserver(handleDomMutations);
    domObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    state.observers.push(domObserver);
  }

  /**
   * Setup retry mechanism for initial render
   */
  function setupRetryMechanism() {
    let retries = 0;
    
    state.retryHandle = setInterval(() => {
      fullPaint();
      
      // Stop retrying when selling plan list appears or max retries reached
      if (document.querySelector(SELECTORS.CUSTOM_SELLING_PLAN_LIST) || ++retries >= CONFIG.MAX_RETRIES) {
        clearInterval(state.retryHandle);
        state.retryHandle = null;
      }
    }, CONFIG.RETRY_INTERVAL_MS);
  }

  /**
   * Cleanup function
   */
  function cleanup() {
    // Disconnect all observers
    state.observers.forEach(observer => observer.disconnect());
    state.observers = [];

    // Clear retry interval
    if (state.retryHandle) {
      clearInterval(state.retryHandle);
      state.retryHandle = null;
    }

    // Remove event listeners
    document.body.removeEventListener('click', handleSubifyToggle);
    document.removeEventListener('change', handleVariantChange);

    state.isInitialized = false;
  }

  /**
   * Main initialization function
   */
  function init() {
    // Prevent duplicate initialization
    if (state.isInitialized) {
      console.warn('custom.js: Already initialized, skipping');
      return;
    }

    initSessionStorage();

    if (!loadProductData()) {
      console.error('custom.js: Failed to load product data, aborting');
      return;
    }

    setupEventListeners();
    
    // Initial render
    fullPaint();
    
    // Setup retry for late-loading elements
    setupRetryMechanism();

    state.isInitialized = true;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STICKY ATC MODULE
  // ═══════════════════════════════════════════════════════════════════════════
  
  const StickyATC = {
    bar: null,
    section: null,
    mainForm: null,
    mainSubmit: null,
    stickyForm: null,
    stickyIdInput: null,
    stickyBtn: null,
    stickyPriceTarget: null,
    priceElement: null,
    observers: [],
    isSimpleBundles: false,
    isInitialized: false,

    /**
     * Mark price sources for sticky ATC
     */
    markPriceSources() {
      const mark = () => {
        // Custom price wrapper
        document.querySelectorAll('#custom-pricing, [data-sticky-price-source]').forEach(el => {
          if (!el.hasAttribute('data-sticky-price-source')) {
            el.setAttribute('data-sticky-price-source', '');
          }
        });

        // Simple Bundles total nodes
        const bundleTotal = document.querySelector('[data-simple-bundles-total]') ||
                           document.querySelector('.simple-bundles__total, .simple-bundles-total, .sb-bundle-total, .sbk-bundle-total-price');

        if (bundleTotal && !bundleTotal.hasAttribute('data-sticky-price-source')) {
          bundleTotal.setAttribute('data-sticky-price-source', '');
        }
      };

      mark();
      const observer = new MutationObserver(mark);
      observer.observe(document.body, { childList: true, subtree: true });
      this.observers.push(observer);
    },

    /**
     * Find main price element
     */
    findMainPriceEl(root = this.section) {
      return root.querySelector(SELECTORS.STICKY_PRICE_SOURCE) ||
             root.querySelector('[data-product-price], .product-details .price, [id^="Price-"] .price, .price');
    },

    /**
     * Copy price from main to sticky
     */
    copyPrice() {
      if (!this.stickyPriceTarget) return;
      
      const source = this.priceElement || this.findMainPriceEl();
      if (source) {
        this.stickyPriceTarget.innerHTML = source.innerHTML;
      }
    },

    /**
     * Get visible button label text
     */
    getVisibleButtonLabel(btn) {
      if (!btn) return '';

      // Prefer common label nodes
      const preferred = btn.querySelector(
        '[data-button-contents], [data-add-to-cart-text], .button__text, .product-form__submit > span'
      );
      if (preferred) return preferred.textContent.trim();

      // Find first visible span
      const visibleSpans = Array.from(btn.querySelectorAll('span, strong, b'))
        .filter(el => {
          const cs = getComputedStyle(el);
          return el.offsetParent !== null && 
                 cs.display !== 'none' && 
                 cs.visibility !== 'hidden';
        });

      return visibleSpans[0] ? visibleSpans[0].textContent.trim() : btn.textContent.trim();
    },

    /**
     * Sync variant ID and button state
     */
    syncVariantAndState() {
      // Sync variant ID
      if (this.mainForm && this.stickyIdInput) {
        const idInput = this.mainForm.querySelector('input[name="id"]');
        if (idInput && idInput.value) {
          this.stickyIdInput.value = idInput.value;
        }
      }

      // Sync button state
      if (this.mainSubmit && this.stickyBtn) {
        this.stickyBtn.disabled = !!this.mainSubmit.disabled;
        
        // Mirror button label
        if (this.mainSubmit.textContent.trim()) {
          this.stickyBtn.textContent = this.getVisibleButtonLabel(this.mainSubmit);
        }
      }
    },

    /**
     * Setup visibility observer
     */
    setupVisibilityObserver() {
      const observeTarget = this.mainForm || this.mainSubmit;
      if (!observeTarget) return;

      const observer = new IntersectionObserver(([entry]) => {
        const shouldShow = !entry.isIntersecting;
        this.bar.classList.toggle('sticky-atc--visible', shouldShow);
        this.bar.classList.toggle('sticky-atc--hidden', !shouldShow);
        this.bar.setAttribute('aria-hidden', String(!shouldShow));
      }, {
        rootMargin: CONFIG.INTERSECTION_ROOT_MARGIN,
        threshold: CONFIG.INTERSECTION_THRESHOLD
      });

      observer.observe(observeTarget);
      this.observers.push(observer);
    },

    /**
     * Setup price observer
     */
    setupPriceObserver() {
      this.priceElement = this.findMainPriceEl();

      // Observe price element changes
      if (this.priceElement) {
        const observer = new MutationObserver(() => this.copyPrice());
        observer.observe(this.priceElement, {
          childList: true,
          subtree: true,
          characterData: true
        });
        this.observers.push(observer);
      }

      // Watch for price element replacement
      const hostObserver = new MutationObserver(() => {
        const newEl = this.findMainPriceEl();
        if (newEl !== this.priceElement) {
          this.priceElement = newEl;
          this.copyPrice();
          
          if (this.priceElement) {
            const observer = new MutationObserver(() => this.copyPrice());
            observer.observe(this.priceElement, {
              childList: true,
              subtree: true,
              characterData: true
            });
            this.observers.push(observer);
          }
        }
      });

      hostObserver.observe(this.section, { childList: true, subtree: true });
      this.observers.push(hostObserver);
    },

    /**
     * Setup event listeners
     */
    setupEventListeners() {
      const reSync = () => {
        this.copyPrice();
        this.syncVariantAndState();
      };

      // Form changes
      if (this.mainForm) {
        this.mainForm.addEventListener('change', reSync);

        // Watch variant input mutations
        const idInput = this.mainForm.querySelector('input[name="id"]');
        if (idInput) {
          const observer = new MutationObserver(reSync);
          observer.observe(idInput, {
            attributes: true,
            attributeFilter: ['value']
          });
          this.observers.push(observer);
        }
      }

      // Custom events
      ['variant:changed', 'sellingplan:changed', 'price:updated', 'bundle:updated', 'bundle:price:updated']
        .forEach(event => {
          document.addEventListener(event, reSync, true);
        });

      // Simple Bundles submit handler
      if (this.isSimpleBundles && this.stickyForm) {
        this.stickyForm.addEventListener('submit', (e) => {
          e.preventDefault();
          if (this.mainSubmit) this.mainSubmit.click();
        });
      }
    },

    /**
     * Detect if this is a Simple Bundles product
     */
    detectSimpleBundles() {
      return !!(
        this.section.querySelector('[data-simple-bundles-root]') ||
        this.section.querySelector('[data-simple-bundles-atc]') ||
        this.section.querySelector('.sb-bundle, .simple-bundles, .sbk-bundle')
      );
    },

    /**
     * Initialize sticky ATC
     */
    init() {
      // Prevent duplicate initialization
      if (this.isInitialized) {
        console.warn('StickyATC: Already initialized, skipping');
        return;
      }

      this.bar = document.querySelector(SELECTORS.STICKY_ATC);
      if (!this.bar) return;

      this.section = this.bar.closest('.shopify-section') || document;

      // Find main form and submit button
      this.mainForm = this.section.querySelector('product-form form') ||
                     this.section.querySelector('.product-form form') ||
                     this.section.querySelector('form[action*="/cart/add"]');

      this.mainSubmit = (this.mainForm && this.mainForm.querySelector('button[type="submit"]')) ||
                       this.section.querySelector('[data-simple-bundles-atc], .sb-bundle-atc, .simple-bundles__atc') ||
                       this.section.querySelector('button[name="add"], .product-form__submit');

      if (!this.mainSubmit && !this.mainForm) return;

      // Find sticky elements
      this.stickyForm = this.bar.querySelector('form');
      this.stickyIdInput = this.stickyForm?.querySelector('input[name="id"]');
      this.stickyBtn = this.stickyForm?.querySelector('button[type="submit"]');
      this.stickyPriceTarget = this.bar.querySelector(SELECTORS.STICKY_PRICE_TARGET);

      this.isSimpleBundles = this.detectSimpleBundles();

      // Setup all functionality
      this.markPriceSources();
      this.setupVisibilityObserver();
      this.setupPriceObserver();
      this.setupEventListeners();

      // Initial sync
      this.copyPrice();
      this.syncVariantAndState();

      this.isInitialized = true;
    },

    /**
     * Cleanup
     */
    cleanup() {
      this.observers.forEach(observer => observer.disconnect());
      this.observers = [];
      this.isInitialized = false;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN EXECUTION
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Ready function
   */
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  /**
   * Main entry point
   */
  function main() {
    init();
    StickyATC.init();
  }

  // Initialize on load
  ready(main);

  // Re-initialize on Shopify section reload
  document.addEventListener('shopify:section:load', () => {
    cleanup();
    StickyATC.cleanup();
    main();
  });

})();